import React, {useState, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import ColumnDefinition from '../../../pieces/grids/columnGenerator';
import * as allConstants from '../../../constants/allConstants';
import {roleTranslator} from '../../../utils/translator';
import StatusRenderer from '../../../pieces/grids/statusRenderer';
import PropTypes from 'prop-types';
import {Box, Typography} from '@mui/material';
import axios from 'axios';
import {toast} from 'react-toastify';
import MyPaginatedGrid from '../../../pieces/grids/paginatedGridWrapper';
import ConfirmModal from '../../../pieces/modals/confirmPopup';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';
import ApiUtils from '../../../utils/apiUtils';
import base_url from '../../../baseUrls';
import {COLORS} from '../../../styles/styleConstants';
import {updateUserState} from '../../../redux/user/userSlice';
import {IPERMIT_ROLES} from '../../../constants/roles';

/** Roles allowed to take another agent off (or back on) AI call duty. */
const AI_AGENT_ADMIN_ROLES = [IPERMIT_ROLES.IPERMIT_ADMIN, IPERMIT_ROLES.SYSTEM_ADMIN];


export const getAiAgentsGridAdditionalButtons = (viewController) => {
  const refreshGrid = () => {
    viewController.refreshGrid();
  };
  return [
    {
      key: 2,
      label: 'Refresh Grid',
      variant: 'outlined',
      color: 'primary',
      onClick: refreshGrid,
    },
  ];
};

function AiAgentsGrid(props) {
  const viewController = props.viewController;
  const dispatch = useDispatch();

  const ls_name = 'ai_agents_grid';
  const [components] = useState({
    statusRenderer: StatusRenderer,
  });
  /** The agent whose AI availability is waiting on confirmation, plus the value we would write. */
  const [pendingAvailabilityChange, setPendingAvailabilityChange] = useState(null);
  const [savingAvailability, setSavingAvailability] = useState(false);

  const currentUser = viewController.user;
  const canManageAgents = AI_AGENT_ADMIN_ROLES.includes(currentUser?.role);

  const closeAvailabilityConfirm = () => {
    if (savingAvailability) return;
    setPendingAvailabilityChange(null);
  };

  const requestAvailabilityChange = (rowData, aiAvailable) => {
    const agentName = `${rowData?.firstName ?? ''} ${rowData?.lastName ?? ''}`.trim();
    setPendingAvailabilityChange({
      userId: rowData?.id,
      name: agentName || rowData?.email || 'this agent',
      aiAvailable: aiAvailable,
    });
  };

  const submitAvailabilityChange = async () => {
    const change = pendingAvailabilityChange;
    if (!change?.userId) return;

    setSavingAvailability(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      await axios.post(
        `${base_url.api}users/update-call-agent`,
        {
          userId: change.userId,
          aiAvailable: change.aiAvailable,
        },
        {
          headers: {
            Authorization: authToken,
          },
        },
      );

      // Keep the navbar switch honest when an admin changes their own availability from here.
      if (change.userId === currentUser?.id) {
        dispatch(updateUserState({aiAvailable: change.aiAvailable}));
      }

      toast(`${change.name} is now ${change.aiAvailable ? 'active' : 'inactive'} for AI calls.`, {type: 'success'});
      setPendingAvailabilityChange(null);
      viewController.refreshGrid();
    } catch (error) {
      const message = error?.response?.data?.message ?? 'Could not update AI availability for this agent.';
      toast(message, {type: 'error'});
    } finally {
      setSavingAvailability(false);
    }
  };

  const getContextMenuItems = (params) => {
    if (!params.node) {
      return [];
    }

    const rowData = params?.node?.data;
    const isAvailable = !!rowData?.aiAvailable;
    const multipleSelected = viewController.selectedMultipleRows();

    return [
      {
        name: isAvailable ? 'Mark Inactive for AI Calls' : 'Mark Active for AI Calls',
        disabled: !canManageAgents || multipleSelected || !rowData?.id,
        action: () => requestAvailabilityChange(rowData, !isAvailable),
      },
      'separator',
      {
        name: 'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel({onlySelected: true}),
      },
      'copy',
    ];
  };

  const modifyDataElement = (el) => {
    const firstName = el?.firstName ?? el?.first_name ?? '';
    const lastName = el?.lastName ?? el?.last_name ?? '';
    return {
      ...el,
      id: el?.id ?? el?.user_id ?? el?.agent_id,
      firstName,
      lastName,
      email: el?.email ?? el?.email_address ?? '',
      role: el?.role ?? '',
      phoneNumber: el?.phoneNumber ?? el?.phone_number ?? el?.phone ?? '',
      status: el?.status != null ? String(el.status) : '',
      aiAvailable: !!(el?.aiAvailable ?? el?.enabled_for_calls ?? el?.enabledForCalls),
      acceptAiCall: !!(el?.acceptAiCall ?? el?.accept_ai_call ?? el?.enabled_for_calls ?? el?.enabledForCalls),
    };
  };

  const columnDefs = useMemo(() => {
    return [
      new ColumnDefinition({field: allConstants.USER_FIELDS.ID, hide: true}),
      new ColumnDefinition({
        field: allConstants.USER_FIELDS.NAME,
        wrapText: true,
        lockPinned: true,
        pinned: 'left',
        checkboxSelection: true,
        width: 300,
        valueGetter: ({data}) => {
          return `${data.firstName} ${data.lastName}`;
        },
      }),
      new ColumnDefinition({field: allConstants.USER_FIELDS.EMAIL}),
      new ColumnDefinition({
        field: allConstants.USER_FIELDS.ROLE,
        valueGetter: ({data}) => roleTranslator.translate(data.role),
        minWidth: 150,
      }),
      new ColumnDefinition({field: allConstants.USER_FIELDS.PHONE_NUMBER, minWidth: 150}),
      new ColumnDefinition({
        field: allConstants.USER_FIELDS.STATUS,
        cellRenderer: 'statusRenderer',
        width: 130,
        filter: 'agSetColumnFilter',
        filterValues: Object.values(allConstants.USER_STATUSES).map((s) => s.value.toUpperCase()),
      }),
      new ColumnDefinition({
        field: allConstants.USER_FIELDS.AI_AVAILABLE,
        sortable: true,
        convertToBoolean: true,
        filter: 'agSetColumnFilter',
        filterValues: ['Yes', 'No'],
        cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
        valueGetter: ({data}) => !!data?.aiAvailable,
      }),
      new ColumnDefinition({field: allConstants.USER_FIELDS.AI_AGENT_ID}),

      // new ColumnDefinition({
      //   field: allConstants.USER_FIELDS.ACCEPT_AI_CALL,
      //   sortable: true,
      //   headerName: allConstants.USER_FIELDS.ACCEPT_AI_CALL.display_label,
      //   convertToBoolean: true,
      //   filter: 'agSetColumnFilter',
      //   filterValues: ['Yes', 'No'],
      //   cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
      //   valueGetter: ({data}) => {
      //     const v = data?.[allConstants.USER_FIELDS.ACCEPT_AI_CALL.api_name];
      //     return !!v;
      //   },
      // }),
     ];
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        pt: 0,
        mt: '-12px',
      }}
    >
      {pendingAvailabilityChange && (
        <ConfirmModal open={true} handleClose={closeAvailabilityConfirm}>
          <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
            {!pendingAvailabilityChange.aiAvailable && (
              <Typography variant={'body1'} color={COLORS.globalRed} mb={2}>
                An inactive agent stops receiving AI calls and live transfers.
              </Typography>
            )}
            <Typography variant={'body1'} mb={3}>
              {'Mark '}
              <Typography component={'span'} fontWeight={600} color={COLORS.globalBlue}>
                {pendingAvailabilityChange.name}
              </Typography>
              {pendingAvailabilityChange.aiAvailable ? ' active for AI calls?' : ' inactive for AI calls?'}
            </Typography>

            <FormSubmitButtons
              cancelText={'No'}
              submitText={'Yes'}
              cancelAction={closeAvailabilityConfirm}
              submitAction={submitAvailabilityChange}
              disableSubmit={savingAvailability}
              disableCancel={savingAvailability}
            />
          </Box>
        </ConfirmModal>
      )}
      <MyPaginatedGrid
        columnDefs={columnDefs}
        components={components}
        contextMenu={getContextMenuItems}
        ls_name={ls_name}
        registerGrid={viewController.setGridToParent}
        registerTempGrid={viewController.setTempGridToParent}
        fetchParams={props.fetchParams}
        modifyDataElement={modifyDataElement}
        onFetchFail={viewController.onFetchFail}
        pagination={props.pagination}
        refreshTimestamp={viewController.refreshTimestamp}
      />
    </Box>
  );
}

AiAgentsGrid.propTypes = {
  pagination: PropTypes.instanceOf(Object).isRequired,
  viewController: PropTypes.instanceOf(Object).isRequired,
  fetchParams: PropTypes.shape({
    url: PropTypes.string.isRequired,
    respKey: PropTypes.string.isRequired,
    options: PropTypes.object,
  }).isRequired,
};

export default AiAgentsGrid;
